/**
 * English interface locale.
 *
 * Тип требует все ключи русского словаря: недостающий перевод теперь роняет
 * сборку, а не обнаруживается тестом на полноту.
 */

import type { Messages } from './types.js';

export const EN_MESSAGES: Messages = {
  'app.title': 'Type2X',
  'app.tagline': 'Adaptive touch-typing trainer',
  'update.available': 'A new version of the app is available.',
  'update.apply': 'Reload',

  'onboarding.title': 'Learn to touch type',
  'onboarding.lead':
    'The trainer watches which keys and combinations are harder for you and builds exercises out of them. Not from a list made for everyone — from yours.',
  'onboarding.setup': 'Layout, system and keyboard form',
  'onboarding.measures':
    'What is measured: intervals between keystrokes and errors per key. The text you type is never sent anywhere.',
  'onboarding.measuresMore': 'What reaches the server',
  'onboarding.measuresBody':
    'Only lesson totals reach the server: how many characters, what accuracy, which keys were hard. The keystrokes themselves and your text stay in this browser.',
  'onboarding.language': 'Learning language',
  'onboarding.language.ru': 'Russian',
  'onboarding.language.en': 'English',
  'onboarding.layout': 'Layout',
  'onboarding.platform': 'Operating system',
  'onboarding.platform.windows': 'Windows',
  'onboarding.platform.macos': 'macOS',
  'onboarding.platform.linux': 'Linux',
  'onboarding.form': 'Keyboard form',
  'onboarding.form.ansi': 'ANSI',
  'onboarding.form.iso': 'ISO',
  'onboarding.layoutId.ru-jcuken': 'JCUKEN',
  'onboarding.layoutId.en-qwerty': 'QWERTY',
  'recommendation.accuracy': 'Slow down and bring accuracy above 92 percent.',
  'recommendation.review': 'Revisit your weak words in the My mistakes mode.',
  'recommendation.speed': 'Accuracy is high — you can raise the pace carefully.',
  'recommendation.control-test': 'Take a control test on unseen text.',
  'recommendation.rest': 'Your rhythm is uneven. Take a break and come back later.',
  'onboarding.start': 'Take the diagnostic',
  'onboarding.guest': 'You are a guest. Progress stays in this browser.',

  'diagnostics.title': 'Diagnostic',
  'diagnostics.hint': 'Type at your usual pace. This is a measurement, not an exam.',

  'practice.title': 'Personal practice',
  'practice.stage.see': 'Look at the word',
  'practice.stage.split': 'Remember the parts',
  'practice.stage.hide': 'Hints are fading',
  'practice.stage.type': 'Type it from memory',
  'practice.stage.apply': 'Use it in a sentence',
  'practice.stage.return': "We'll come back to it later",

  'mistakes.title': 'My mistakes',
  'mistakes.hint': 'Working on the word “{word}” step by step.',
  'mistakes.stages': 'Loop steps',
  'mistakes.stagesHint': 'The steps advance on their own — there is nothing to click.',
  'mistakes.chunks': 'Word chunks',
  'mistakes.progress': 'Words completed',
  'mistakes.remaining': 'Words left: {count}',
  'mistakes.emptyTitle': 'No weak spots',
  'mistakes.empty': 'No weak spots left. Come back after a practice session.',
  'mistakes.stage.see': 'Look at the word “{word}” and type it.',
  'mistakes.stage.split': 'Remember the chunks and type the whole word.',
  'mistakes.stage.hide': 'Hints fade: only the zone stays highlighted.',
  'mistakes.stage.type': 'The keyboard is hidden. Type the word from memory.',
  'mistakes.stage.apply': 'Use the word in a sentence.',
  'onboarding.startPath': 'Where to start',
  'onboarding.start.fundamentals': 'With the basics: I cannot touch type yet',
  'onboarding.start.diagnostics': 'With a diagnostic: I already type somehow',
  'onboarding.startBasics': 'Start with the basics',
  'fundamentals.title': 'Basics',
  'fundamentals.typing': 'Drill',
  'fundamentals.hint': 'Fingers rest on the home row. Look at the text, not at the keyboard.',
  'fundamentals.group': 'Learning now: {row}, {finger}.',
  'fundamentals.current': 'Learning now:',
  'fundamentals.unlockedCount': 'Keys open: {open} of {total} — {keys}',
  'fundamentals.progress': 'Key groups completed',
  'fundamentals.chunks': 'Short combinations for now: the movement matters, not the meaning.',
  'fundamentals.words': 'The unlocked keys are enough for real words — moving on to them.',
  'fundamentals.done': 'Every key is unlocked. Personal practice comes next.',
  'fundamentals.row.home': 'home row',
  'fundamentals.row.top': 'top row',
  'fundamentals.row.bottom': 'bottom row',
  'fundamentals.finger.index': 'index fingers',
  'fundamentals.finger.middle': 'middle fingers',
  'fundamentals.finger.ring': 'ring fingers',
  'fundamentals.finger.pinky': 'little fingers',
  // Modes 8-10: digits, punctuation and special characters.
  'symbols.title.digits': 'Digits',
  'symbols.title.punctuation': 'Punctuation',
  'symbols.title.special-characters': 'Special characters',
  'symbols.typing': 'Drill',
  'symbols.group': 'Learning now: {characters}.',
  'symbols.unlockedCount': 'Symbols open: {open} of {total} — {keys}',
  'symbols.progress': 'Symbol groups completed',
  'symbols.shift': 'Symbols typed with Shift',
  'symbols.shiftHint': '{character} is Shift plus the {base} key.',
  'symbols.hint.digits': 'Digits come in groups of three: the movement matters, not the number.',
  'symbols.hint.punctuation': 'Punctuation lives inside a sentence: type the whole line.',
  'symbols.hint.special-characters': 'Press Shift with the little finger of the opposite hand.',
  'symbols.done': 'Every symbol of this mode is unlocked.',
  // Modes 6 and 7: speed and accuracy.
  'tempo.title.speed': 'Speed',
  'tempo.title.accuracy': 'Accuracy',
  'tempo.typing': 'Run',
  'tempo.goal.speed': 'Target: {cpm} characters per minute without losing accuracy.',
  'tempo.goal.accuracy': 'Calm pace: around {cpm} characters per minute.',
  'tempo.hint': 'The step rises once the target is held twice in a row.',
  'tempo.stage.speed': 'Do not look at the keyboard: that look is what keeps you slow.',
  'tempo.stage.accuracy': 'Take your time: a line counts only without a single error.',
  'tempo.progress': 'Runs completed',
  'tempo.done': 'The session is over.',
  'tempo.verdict.held': 'Target reached. One more and the step rises.',
  'tempo.verdict.advanced': 'Step up: the target is higher now.',
  'tempo.verdict.slow': 'Accuracy is fine, but the target speed was not reached.',
  'tempo.verdict.dropped': 'Accuracy dropped — the step went back down.',
  'tempo.verdict.clean': 'Clean, not a single error.',
  'tempo.verdict.retry': 'There was an error: the line starts over.',
  'tempo.verdict.deferred': 'The line is put off until next time.',
  // Modes 11 and 12: developer pack and email/web pack.
  'packs.title.developer-pack': 'Developer Pack',
  'packs.title.email-web-pack': 'Email and Web Pack',
  'packs.typing': 'Drill',
  'packs.group': 'Learning now: {tokens}.',
  'packs.unlockedCount': 'Combinations open: {open} of {total} — {tokens}',
  'packs.progress': 'Combination groups completed',
  'packs.shift': 'Symbols typed with Shift',
  'packs.shiftHint': '{character} is Shift plus the {base} key.',
  'packs.layout': 'This drill runs on the Latin layout: switch to it before you start.',
  'packs.hint.developer-pack': 'An operator is typed whole: one movement, not two halves.',
  'packs.hint.email-web-pack':
    'The addresses are made up: none of these domains or mailboxes exist.',
  'packs.done': 'Every combination of this pack is unlocked.',
  // Layout switching drill (spec 8.1).
  'layoutSwitch.title': 'Layout switching',
  'layoutSwitch.typing': 'Drill',
  'layoutSwitch.explain': 'The language changes with every fragment: switch the layout yourself.',
  'layoutSwitch.segments': 'Fragments of this drill',
  'layoutSwitch.boundary': '— the layout changes here',
  'layoutSwitch.count': 'Layout switches in this drill: {count}.',
  'layoutSwitch.language.ru': 'Cyrillic:',
  'layoutSwitch.badge.ru': 'RU',
  'layoutSwitch.badge.en': 'EN',
  'layoutSwitch.language.en': 'Latin:',
  'layoutSwitch.hint': 'The keyboard is hidden: there are two layouts and room for one.',

  'modes.group.learn': 'Learn',
  'modes.group.signs': 'Signs and digits',
  'modes.group.latin': 'Latin script',
  'modes.group.own': 'Speed and your own text',
  'modes.lockedWhy':
    'The material is typed in a layout you have not learned yet: until its letters come easily, the drill will be errors rather than practice. You can still look inside.',
  'modes.tryAnyway': 'Try it anyway',
  'modes.locked.en':
    'Code, addresses and layout switching are typed in Latin. These modes open once every Latin letter is typed confidently: {mastered} of {total} so far.',
  'modes.locked.ru':
    'Layout switching runs in two layouts, and the second one is Cyrillic. The mode opens once every Cyrillic letter is typed confidently: {mastered} of {total} so far.',
  'modes.train.en': 'Practise Latin',
  'modes.train.ru': 'Practise Cyrillic',
  'nav.forward': 'Forward',
  'nav.label': 'Navigation',
  'nav.back': 'Back',
  'nav.account.checking': 'Checking',
  'nav.account.checkingAria': 'Checking whether you are signed in. Open account',
  'nav.account.guest': 'Guest',
  'nav.account.guestAria': 'You are not signed in, progress stays in this browser. Open account',
  'nav.account.signedIn': 'Signed in',
  'nav.account.signedInAria': 'You are signed in, email confirmed. Open account',
  'nav.account.unverified': 'Unconfirmed',
  'nav.account.unverifiedAria': 'You are signed in, email not confirmed yet. Open account',
  'home.title': 'What next',
  'home.explain':
    'Continue what you started, take another mode or change where you began — everything is here.',
  'home.continue': 'Continue practice',
  'home.startPath': 'Where the training starts',
  'home.look': 'Review and save',
  'legal.privacy': 'Privacy policy',
  'legal.terms': 'Terms of use',
  'legal.agreement': 'By registering you accept the terms of use and the privacy policy.',
  'legal.analytics':
    'Analytics is off by default: without your consent we collect nothing beyond what training needs.',
  'language.training': 'Practice language',
  'language.setupTitle': 'New language: {language}',
  'language.setupHint':
    'Progress in the previous language stays where it was: you can come back any time.',
  'language.setupCancel': 'Back to the previous language',
  // Appearance: theme and focus mode (spec UX-01, UX-03).
  'appearance.theme': 'Colour theme',
  'appearance.theme.system': 'Match system',
  'appearance.theme.light': 'Light',
  'appearance.theme.dark': 'Dark',
  'appearance.focusOn': 'Focus',
  'appearance.focusOff': 'Leave focus',
  'appearance.focusEntered': 'Distraction-free mode on',
  'appearance.soundOn': 'Sound: on',
  'appearance.soundOff': 'Sound: off',
  'appearance.gameOn': 'Rewards: on',
  'appearance.gameOff': 'Rewards: off',

  // Settings live behind one button instead of a row of bars (SRS FR-EN-83).
  'settings.title': 'Settings',
  'settings.close': 'Close settings',
  // A drill shows its text first; the rest opens on demand (SRS FR-EN-84).
  'drill.details': 'About this drill',
  'home.tune': 'Adjust the training',

  // Ступени набора. Их называют два экрана — диагностика и контрольный тест,
  // — поэтому ключи названы по шкале, а не по экрану, где она появилась
  // первой: две копии слова «Уверенный» рано или поздно разойдутся.
  'skillLevel.searching': 'Hunting for keys',
  'skillLevel.beginner': 'Beginner',
  'skillLevel.confident': 'Confident',
  'skillLevel.fast': 'Fast',
  'skillLevel.professional': 'Professional',
  'skillLevel.effectiveCpm': '{cpm} characters per minute, adjusted for accuracy',

  'diagnosticResult.title': 'Your level',
  'diagnosticResult.explain.searching':
    'Every key is still found one at a time. That is a normal start: speed arrives once the hands remember the layout.',
  'diagnosticResult.explain.beginner':
    'The keyboard is familiar, but the fingers still pick their way. What grows next is evenness rather than speed.',
  'diagnosticResult.explain.confident':
    'An ordinary working pace: text gets typed without demanding attention. What holds you back now are single weak combinations, not the keyboard.',
  'diagnosticResult.explain.fast':
    'Above average. At this level people grow through accuracy: corrections cost more than acceleration gains.',
  'diagnosticResult.explain.professional':
    'Professional speed. Practice here is about rare combinations, symbols and layout switching, not letters.',
  'diagnosticResult.accurateButSlow':
    'You almost never miss, but about a second passes between keystrokes. That happens in two cases: when the keys are found by eye, and when the movement is right but not yet automatic. Only you know which; the fundamentals help with both.',
  'diagnosticResult.weakSpots': 'What comes hardest',
  'diagnosticResult.nextStep': 'What next',
  'diagnosticResult.recommend.fundamentals':
    'We suggest starting with the fundamentals: keys open in groups and the hands settle into place.',
  'diagnosticResult.recommend.practice':
    'We suggest personal practice: the material is chosen from your weak spots.',
  'diagnosticResult.continue.fundamentals': 'Start with fundamentals',
  'diagnosticResult.continue.practice': 'Go to practice',
  'diagnosticResult.retake': 'Take it again',
  'diagnosticResult.notAVerdict':
    'This is a starting point, not a verdict: your level is recalculated after every session, and one bad measurement changes nothing.',
  // Skill report by keys, combinations and words (spec UX-07).
  'report.summary': 'Units tracked: {tracked}. Mastered: {mastered}. Due for review: {due}.',
  'report.weakest': 'What is hardest right now',
  'report.heatLegend': 'How far from mastered',
  'report.title': 'Skill report',
  'report.open': 'Skill report',
  'report.back': 'Back to practice',

  'achievements.title': 'Achievements',
  'quests.period.day': 'Today',
  'quests.period.week': 'This week',
  'quests.title': 'Quests',
  // Seasonal challenge of the month (ТЗ FR-GM-07).
  'season.title': 'Season',
  'season.name': '{month} {year}',
  'season.explain':
    'A season is one calendar month with a single goal. The challenge is known in advance and repeats in turn; an unfinished season takes nothing away.',
  'season.challenge.practice-days': 'Practise on {target} days this month.',
  'season.challenge.characters': 'Type {target} characters this month.',
  'season.challenge.clean-days': 'Have {target} days with accuracy of 95 percent or better.',
  'season.month.1': 'January',
  'season.month.2': 'February',
  'season.month.3': 'March',
  'season.month.4': 'April',
  'season.month.5': 'May',
  'season.month.6': 'June',
  'season.month.7': 'July',
  'season.month.8': 'August',
  'season.month.9': 'September',
  'season.month.10': 'October',
  'season.month.11': 'November',
  'season.month.12': 'December',
  'quests.explain':
    'Weekly quests count the last seven days, not the calendar week: Sunday evening resets nothing.',
  'quests.done': 'done',
  'quests.name.daily-session': 'Today: finish a session',
  'quests.name.daily-characters': 'Today: type 300 characters',
  'quests.name.daily-accuracy': 'Today: a session at 95% accuracy',
  'quests.name.weekly-days': 'This week: practise on 4 days',
  'quests.name.weekly-characters': 'This week: type 1200 characters',
  'quests.name.weekly-accuracy': 'This week: three clean days',
  'achievements.open': 'Achievements',
  'achievements.summary':
    'Milestones reached: {earned} of {total}. Achievements are free and stay yours.',
  'achievements.streak': 'Practice streak',
  'achievements.streakDays': 'Days in a row: {days}',
  'achievements.streakBest': 'Longest streak: {days}. Days practised in total: {total}.',
  'achievements.records': 'Personal records',
  'achievements.bestCpm': 'Best session speed: {cpm} characters per minute',
  'achievements.bestAccuracy': 'Best session accuracy: {accuracy}%',
  'achievements.bestVerified': 'Best verified result: {wpm} words per minute',
  'achievements.typed': 'Characters typed in total: {characters}',
  'achievements.list': 'Milestones',
  'achievements.unlocked': 'earned',
  'achievements.name.first-thousand': 'First thousand',
  'achievements.about.first-thousand': 'Type your first thousand characters.',
  'achievements.name.first-verified': 'No hints',
  'achievements.about.first-verified':
    'Pass a control test: unseen text, hidden keyboard, no corrections. The server confirms the result, so an account is required.',
  'achievements.name.accuracy-95': 'Accuracy 95%',
  'achievements.about.accuracy-95': 'Finish a session with accuracy of 95% or better.',
  'achievements.name.ten-words': 'Ten fixed',
  'achievements.about.ten-words': 'Bring ten problem words up to confident typing.',
  'achievements.name.seven-days': 'A week of practice',
  'achievements.about.seven-days': 'Practise on seven days — they need not be consecutive.',
  'achievements.name.verified-35-wpm': 'Thirty-five',
  'achievements.about.verified-35-wpm': 'Reach 35 words per minute on unseen text.',
  'achievements.name.symbols-confident': 'Signs and symbols',
  'achievements.about.symbols-confident':
    'Type ten punctuation marks and special symbols confidently.',
  'achievements.name.two-languages': 'Two languages',
  'achievements.about.two-languages': 'Reach the target result on unseen text in both languages.',
  'report.explain': 'Everything you have typed: error rate, delay and review date.',
  'report.section.keys': 'Keys',
  'report.section.combinations': 'Combinations',
  'report.section.words': 'Words',
  'report.section.switches': 'Layout switching',
  'report.column.unit': 'Item',
  'report.column.attempts': 'Presses',
  'report.column.errors': 'Errors',
  'report.column.latency': 'Delay',
  'report.column.mastery': 'Mastery',
  'report.column.review': 'Review',
  'report.ms': '{value} ms',
  'report.mastered': 'mastered',
  'report.learning': 'in progress',
  'report.emptySectionTitle': 'This section is empty',
  'report.emptySection': 'Nothing typed here yet.',
  'report.emptyAllTitle': 'The report is empty',
  'report.emptyAll': 'The report has nothing to show yet: run your first drill.',
  'report.shown': 'Showing {shown} of {total}.',
  'report.search': 'Search: {section}',
  'report.filterGroup': 'What to show: {section}',
  'report.pageGroup': 'Pages: {section}',
  'report.filter.all': 'All',
  'report.filter.weak': 'Weak',
  'report.filter.due': 'Due for review',
  'report.filter.mastered': 'Mastered',
  'report.nothingFoundTitle': 'Nothing found',
  'report.nothingFound': 'Nothing found. Change the query or the filter.',
  'report.resetSearch': 'Clear search and filter',
  'report.page': 'Page {page} of {pages}',
  'report.prev': 'Back',
  'report.next': 'Next',
  'report.untouched': 'Never typed yet: {count}.',
  // Mode 13: custom text.
  'customText.title': 'Custom text',
  'customText.begin': 'Start the drill',
  'customText.label': 'Paste the text you want to practise on',
  'customText.local': 'The text is parsed on this device and is never sent anywhere.',
  'customText.typing': 'Drill',
  'customText.hint': 'Words from your text: the more often it appears, the more often it returns.',
  'customText.words': 'Words from the text in play: {count}.',
  'customText.rejected': 'Skipped words that this layout cannot type: {count} ({characters}).',
  'customText.tooShort':
    'At least {count} different words are needed, otherwise the drill is just a repeat.',
  'sentences.title': 'Sentences',
  'sentences.typing': 'Typing the sentence',
  'sentences.hint': 'Type the whole sentence, punctuation included.',
  'sentences.focus': 'This sentence was picked for the word “{word}”.',
  'sentences.transfer': 'Plain text: checking whether the skill holds.',
  'sentences.progress': 'Sentences completed',
  'sentences.remaining': 'Sentences left: {count}',
  'sentences.emptyTitle': 'No sentences yet',
  'sentences.empty': 'There are no sentences for this language yet.',

  'control.title': 'Control test',
  'control.hint': 'Unseen text, no hints. Type calmly and accurately.',
  'control.rules': 'Test rules',
  'control.rule.unfamiliar': 'The text is unfamiliar: it does not repeat your exercises.',
  'control.rule.noCorrections': 'No corrections: Backspace does not work during the test.',
  'control.rule.noHints': 'No hints: the keyboard and key highlighting are hidden.',
  'control.rule.progressKept':
    'A result that is not counted does not undo your progress: only the rating is affected.',
  'control.begin': 'Start the test',
  'control.badge': 'Test',
  'control.retentionTitle': 'Delayed check',
  'control.retentionHint': "Let's see whether the skill survived the break.",
  'control.start': 'Take the control test',
  'control.startRetention': 'Take the delayed check',
  'control.accepted': 'Result confirmed.',
  'control.rejected': 'The result does not count towards the rating. Progress is kept.',
  'control.retentionCounted': 'The skill held up after the break.',
  'control.rating': 'Rating: {rating}',
  'control.levelTitle': 'Level from this check:',
  'control.levelUnknown': 'This run is too small to name a level.',
  'control.belowRequirements': 'The result is below the minimum requirements and does not count.',
  'control.waiting': 'Sending the result…',
  'control.needsAccount':
    'The result is not counted: verified results live in an account. Your training progress stayed in this browser.',
  'control.notAccepted': 'The server did not accept the result. Try again later.',
  'control.offline': 'No connection. The result was not sent.',
  'control.back': 'Back to practice',
  'result.retentionDue': 'Time to check whether the skill survived the break.',
  'recommendation.retention': 'Take the delayed check: it confirms the skill has settled.',
  'metrics.wpm': 'WPM',
  'metrics.cpm': 'CPM',
  'metrics.accuracy': 'Accuracy',
  'metrics.rhythm': 'Rhythm',
  'metrics.corrections': 'Corrections',

  'result.done': 'Lesson finished.',
  'result.noWeakUnitsTitle': 'No weak spots in this lesson',
  'result.noWeakUnits': 'No mistakes in this lesson.',
  'result.title': 'Result',
  'result.baseline': 'Starting level',
  'result.current': 'Now',
  'result.growth': 'Growth',
  'result.weakUnits': 'Weak spots',
  'result.achievementEarned': 'New milestone: {names}',
  'result.xpEarned': 'XP earned: {amount}',
  'result.level': 'Level {level}',
  'result.nextStep': 'Next step',
  'result.otherModes': 'Other modes',
  // Кнопка называет режим, а не просто обещает занятие: экран результата один
  // на все режимы, и «ещё одна тренировка» уводила из того, чем занимались.
  'result.againMode': 'Again: {mode}',
  'result.continueTo': 'Continue: {mode}',
  'result.verifiedHint': 'Confirm your result on unseen text so it counts towards the rating.',

  'account.title': 'Save your progress',
  'account.explain': 'Signing up moves progress from this browser and opens 14 days of Premium.',
  'account.email': 'Email',
  'account.password': 'Password',
  'account.submit': 'Sign up and move progress',
  'account.accessOpen': 'Everything is open',
  'account.accessOpenHint':
    'Every mode is available with no limits. Your progress, achievements and history stay with you.',
  'account.accessUnverified': 'Address not confirmed',
  'account.privacyTitle': 'Privacy',
  'account.claimed': 'Progress moved to your account.',
  'account.verified': 'Email address confirmed.',
  'account.signedInAs': 'Signed in as {email}.',
  'account.awaitingAddress': 'The letter went to {email}.',
  'account.progressAlready': 'This browser\u2019s progress was already in the account.',
  'account.progressTaken':
    'This browser\u2019s progress belongs to another account and stayed where it was.',
  'account.login': 'Sign in',
  'account.loginHint': 'Already have an account? Enter the same email and password and sign in.',
  'account.unverified': 'The address is not confirmed yet, so Premium is not open.',
  'account.forgot': 'Forgot your password?',
  'account.resetRequested': 'If that address is registered, an email with a link has been sent.',
  'reset.title': 'New password',
  'reset.explain': 'Set a new password. Existing sessions will be closed, so sign in again.',
  'reset.password': 'New password',
  'reset.submit': 'Save the password',
  'reset.done': 'The password has been changed. Sign in with the new one.',
  'reset.toLogin': 'Go to sign in',
  'privacy.title': 'Your data',
  'privacy.explain':
    'You can take your progress with you: the file holds everything we know about you.',
  'privacy.dangerTitle': 'Danger zone',
  'privacy.dangerExplain': 'This deletes the account for good. The action cannot be undone.',
  'privacy.export': 'Download my progress',
  'privacy.delete': 'Delete account',
  'privacy.deleteWarning':
    'Deletion is irreversible: the account, progress and results go with it.',
  'privacy.deletePassword': 'Password to confirm',
  'privacy.deleteConfirm': 'Confirm deletion',
  'privacy.deleted': 'The account is deleted. Progress in this browser is erased too.',
  // Выход — не опасное действие: аккаунт остаётся, навык возвращается при
  // следующем входе. Но браузер он освобождает целиком, и об этом сказано.
  'logout.title': 'Sign out',
  'logout.explain':
    'Your progress stays in the account and comes back when you sign in again — here or on another device. It leaves this browser: on a shared computer what is yours should not go to the next person.',
  'logout.action': 'Sign out',
  'logout.pending':
    'The results of your latest sessions have not reached the server yet. Sign out now and nothing will bring them back.',
  'logout.force': 'Sign out anyway',
  'account.continue': 'Back to practice',
  'account.awaiting':
    'We sent a link to your email. Follow it to confirm the address and move your progress.',
  'account.awaitingHint':
    'Your progress stays in this browser and is not lost while the address is unconfirmed.',
  'account.resend': 'Send the email again',
  'account.resent': 'The email has been sent again.',
  'account.verifying': 'Confirming the address…',
  'account.verifyFailed': 'The link is invalid or has expired. Ask for a new email.',
  'account.error.invalid-email': 'Check the email address.',
  'account.error.weak-password': 'The password must be at least 10 characters.',
  'account.error.invalid-credentials': 'Wrong email or password.',
  'account.error.progress-taken': 'This browser\u2019s progress belongs to another account.',
  'account.error.rate-limited': 'Too many attempts. Try again later.',
  'account.error.offline': 'No connection. Progress stays saved locally.',
  'account.error.server-error': 'Could not finish signing up. Try again later.',
  'friends.title': 'Friends',
  'friends.explain':
    'The friends rating links people by an invite code: no email address is revealed. Share your own code with anyone; never share someone else’s — it adds them as a friend.',
  'friends.yourCode': 'Your code:',
  'friends.link': 'Invite link',
  'friends.inviteTitle': 'Someone invites you as a friend',
  'friends.invite':
    'You opened the app through an invite link with the code {code}. Add this person as a friend? They will see your nickname and your place in the rating — nothing else.',
  'friends.inviteAccept': 'Add as a friend',
  'friends.inviteDecline': 'Do not add',
  'friends.addLabel': 'A friend’s code',
  'friends.add': 'Add a friend',
  'friends.remove': 'Remove',
  'friends.emptyTitle': 'No friends yet',
  'friends.empty': 'Exchange codes with whoever you want to compare progress with.',
  'friends.boardTitle': 'Among friends',
  'friends.notParticipating': 'not taking part in the rating',
  'friends.unavailable': 'Friends come with an account.',
  'friends.error.not-found': 'There is no such code. Check that it was copied in full.',
  'friends.error.full': 'The friends list is full. Remove someone to add a new one.',
  'friends.error.failed': 'Could not add. Try again later.',
  'leaderboard.title': 'Rating',
  'leaderboard.explain':
    'Your place is computed by the server from accepted control tests. Taking part is your choice: by default you are not in the table.',
  'leaderboard.loading': 'Asking the server…',
  'leaderboard.unavailableTitle': 'The rating needs an account',
  'leaderboard.unavailable':
    'A place is computed from control tests, and those belong to an account. Your progress stays yours without one.',
  'leaderboard.emptyTitle': 'The table is empty so far',
  'leaderboard.empty': 'There is not a single accepted control result in this language yet.',
  'leaderboard.computed': 'Snapshot taken at {moment} UTC, formula {version}.',
  'leaderboard.you': 'you',
  'leaderboard.name': 'Nickname in the table',
  'leaderboard.saveName': 'Save the nickname',
  'leaderboard.saveFailed': 'The server did not accept the change. Try again later.',
  'leaderboard.kinds': 'Rating kind',
  'leaderboard.kind.progress': 'Progress',
  'leaderboard.kind.skill': 'Skill',
  'leaderboard.kind.accuracy': 'Accuracy',
  'leaderboard.kind.consistency': 'Consistency',
  'leaderboard.kind.weekly-growth': 'Weekly growth',
  'leaderboard.hint.progress':
    'Ordered by the main rating: growth since the first measurement, skill, accuracy, retention and variety of modes.',
  'leaderboard.hint.skill': 'Ordered by the speed of the best accepted control test.',
  'leaderboard.hint.accuracy':
    'Ordered by the accuracy of the best accepted test: accuracy at your own speed, not in a deliberately slow run.',
  'leaderboard.hint.consistency':
    'Ordered by days with a check over four weeks and by how well the skill holds in a delayed check.',
  'leaderboard.hint.weekly-growth':
    'Ordered by speed growth over the week. Anyone without a check this week is not in this table: there is nothing to measure.',
  'leaderboard.column.metric.consistency': 'Regularity',
  'leaderboard.column.metric.weekly-growth': 'Growth',
  'leaderboard.column.rank': 'Place',
  'leaderboard.column.name': 'Participant',
  'leaderboard.column.rating': 'Rating',
  'leaderboard.column.wpm': 'Words per minute',
  'leaderboard.column.accuracy': 'Accuracy',
  'leaderboard.consent': 'Take part in the rating',
  'leaderboard.consentHint': 'By default you do not appear in any leaderboard.',
  'keyboard.requiredTitle': 'A physical keyboard is needed here',
  'keyboard.requiredBody':
    'Touch typing is trained on real keys: a phone keyboard on screen will not do. Open Type2X on a computer — or connect a keyboard and press any key on it.',
  'keyboard.requiredWorks':
    'The rest works on a phone: progress, the skill report, achievements and your account.',
  'diagnosticResult.analysed': 'Measured over {count} keystrokes.',
  'diagnosticResult.sampleLimited':
    'There were {count} keystrokes — too few for a steady measurement. The numbers below are rough.',
  'diagnosticResult.definitions': 'What these numbers mean',
  'metrics.explain.wpm': 'Speed is words per minute: five characters count as a word.',
  'metrics.explain.accuracy':
    'Accuracy is the share of keys hit on the first try. A corrected mistake is still a mistake.',
  'metrics.explain.rhythm':
    'Rhythm is how even the intervals between keystrokes are. Evenness beats bursts.',
  'common.locked': 'Locked',
  'home.mastered': '{mastered} of {total} keys mastered',
  'home.continueTo': 'Continue: {mode}',
  'home.legend': 'Mastered keys are filled in',
  'fundamentals.purpose': 'Keys in groups: the home row first, then the rest.',
  'mistakes.purpose': 'The words you miss most often.',
  'sentences.purpose': 'Connected text with punctuation and capitals.',
  'symbols.purpose.digits': 'The number row, without looking at the keyboard.',
  'symbols.purpose.punctuation': 'Periods, commas, dashes and quotes.',
  'symbols.purpose.special-characters': 'Brackets, slashes and upper-register signs.',
  'tempo.purpose.speed': 'Short sprints for pace, without stopping.',
  'tempo.purpose.accuracy': 'Slowly and without a single mistake.',
  'packs.purpose.developer-pack': 'Code: brackets, operators, variable names.',
  'packs.purpose.email-web-pack': 'Email addresses and links in full.',
  'layoutSwitch.purpose': 'Switching from one layout to the other and back.',
  'customText.purpose': 'Your own text instead of ours.',
};
